import { selectCurrentHotelId } from 'accessors'
import CopyIcon from 'components/ReusableComponents/Icons/CopyIcon'
import EmailIcon from 'components/ReusableComponents/Icons/Email'
import RefreshIcon from 'components/ReusableComponents/RefreshIcon'
import useClickOut from 'hooks/useClickOut'
import useCopyText from 'hooks/useCopyText'
import { useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import styles from './WidgetSnippet.module.scss'

const Wrapper = ({ children }) => {
  const [isActive, setIsActive] = useState(false)

  const wrapperRef = useRef()

  useClickOut(wrapperRef, () => {
    setIsActive(false)
  })

  return (
    <div
      className={styles.wrapper}
      ref={wrapperRef}
      onClick={() => setIsActive(true)}
      style={{ bottom: isActive ? 0 : -212, cursor: isActive ? 'auto' : 'pointer' }}
    >
      {children}
    </div>
  )
}

const Snippet = ({ isOnline, onCheckClick, isChecking, hasTitle }) => {
  const currentHotelId = useSelector(selectCurrentHotelId)
  const copy = useCopyText()

  const snippet = `<script src="https://api-core.directful.com/api/widgets?id=${currentHotelId}"></script>`

  const emailText = `To implement Directful widgets, add the following script tag to your homepage, right before the closing </body> tag. 
  %0d%0a
  %0d%0a${snippet}`

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        {hasTitle && (
          <span className={styles.title}>
            <svg
              width="26"
              height="26"
              viewBox="0 0 26 26"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10.1827 17.9833L5.19935 13L10.1827 8.01667L8.66602 6.5L2.16602 13L8.66602 19.5L10.1827 17.9833ZM15.816 17.9833L20.7993 13L15.816 8.01667L17.3327 6.5L23.8327 13L17.3327 19.5L15.816 17.9833V17.9833Z"
                fill="#A8ADB2"
              />
            </svg>
            Widget snippet
          </span>
        )}
        {isOnline && (
          <div className={styles.status}>
            <svg width={16} height={16} viewBox="0 0 16 16" fill="none">
              <circle cx={8} cy={8} r={5} fill="#66BB6A" />
            </svg>
            Snippet is online
          </div>
        )}
        {!isOnline && (
          <div className={styles.status}>
            <svg width={16} height={16} viewBox="0 0 16 16" fill="none">
              <circle cx={8} cy={8} r={5} fill="#FF7043" />
            </svg>
            Widget snippet is not found on your website
          </div>
        )}
      </div>
      <div className={styles.textareaHolder}>
        <textarea className={styles.textarea} value={snippet}></textarea>
        <div className={styles.buttons}>
          <button className={styles.copyButton} onClick={() => copy(snippet)}>
            <CopyIcon />
            Copy code
          </button>
          <button className={styles.refreshButton} onClick={onCheckClick}>
            <RefreshIcon className={isChecking ? styles.refreshingIcon : ''} />
            Check again
          </button>
        </div>
      </div>
      <div className={styles.info}>
        <p className={styles.infoText}>
          Add this code to your homepage HTML, right before closing of the {` `}
          <code>{`</body>`}</code> tag.
        </p>
        <a
          target="_blank"
          href={`mailto:?to=&body=${emailText}&subject=Implementing%Directful%20widgets`}
          className={styles.shareWithITLink}
          rel="noreferrer"
        >
          <EmailIcon /> Share this with your IT professional
        </a>
      </div>
    </div>
  )
}

const WidgetSnippet = ({
  hasWrapper = true,
  isOnline,
  onCheckClick,
  isChecking,
  hasTitle = true
}) => {
  return hasWrapper ? (
    <Wrapper>
      <Snippet
        isOnline={isOnline}
        onCheckClick={onCheckClick}
        isChecking={isChecking}
        hasTitle={hasTitle}
      />
    </Wrapper>
  ) : (
    <Snippet isOnline={isOnline} onCheckClick={onCheckClick} isChecking={isChecking} />
  )
}

export default WidgetSnippet
